// The dev server works a bit differently, so it produces a different URL for the worklet
// We also need to massage TS to allow the url imports

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - query params are not recognized by typescript
import MyWorkletProcessorUrlProd from './VolumeAnalyzerWorklet?worker&url'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - query params are not recognized by typescript
import MyWorkletProcessorUrl from './VolumeAnalyzerWorklet?url'

import { audioContextController } from './AudioContextController'

import type {
  AudioAnalyserOptions,
  LocalAudioTrack,
  LocalTrack,
  Track,
} from 'livekit-client'

/**
 * Creates and returns an analyser web audio node that is attached to the provided track.
 * Additionally returns a convenience method `calculateVolume` to perform instant volume readings on that track.
 * Call the returned `cleanup` function to tear down the nodes and release this
 * helper's lease on the shared audio context.
 */
export function createAudioAnalyser(
  track: LocalTrack<Track.Kind.Audio>,
  options?: AudioAnalyserOptions
) {
  const opts = {
    cloneTrack: false,
    fftSize: 2048,
    smoothingTimeConstant: 0.8,
    minDecibels: -100,
    maxDecibels: -80,
    ...options,
  }
  const lease = audioContextController.acquire('createAudioAnalyser')

  if (!lease) {
    throw new Error('Audio Context not supported on this browser')
  }
  const audioContext = lease.context
  // Only the clone is ours to stop — without cloneTrack this is the caller's
  // own track, still in use after we fail. Held outside the try so the catch
  // can reach it; a clone abandoned there keeps capturing the mic for the life
  // of the page.
  let clonedTrack: MediaStreamTrack | null = null
  try {
    const streamTrack = opts.cloneTrack
      ? (clonedTrack = track.mediaStreamTrack.clone())
      : track.mediaStreamTrack
    const mediaStreamSource = audioContext.createMediaStreamSource(
      new MediaStream([streamTrack])
    )
    const analyser = audioContext.createAnalyser()
    analyser.minDecibels = opts.minDecibels
    analyser.maxDecibels = opts.maxDecibels
    analyser.fftSize = opts.fftSize
    analyser.smoothingTimeConstant = opts.smoothingTimeConstant

    mediaStreamSource.connect(analyser)
    const dataArray = new Uint8Array(analyser.frequencyBinCount)

    /**
     * Calculates the current volume of the track in the range from 0 to 1
     */
    const calculateVolume = () => {
      analyser.getByteFrequencyData(dataArray)
      let sum = 0
      for (const amplitude of dataArray) {
        sum += Math.pow(amplitude / 255, 2)
      }
      const volume = Math.sqrt(sum / dataArray.length)
      return volume
    }

    // The context is shared, so tearing down means disconnecting our own nodes
    // rather than closing it. Anything left connected keeps being pulled
    // through the graph for the life of the page.
    const cleanup = async () => {
      mediaStreamSource.disconnect()
      analyser.disconnect()
      if (opts.cloneTrack) {
        streamTrack.stop()
      }
      lease.release()
    }

    return { calculateVolume, analyser, cleanup }
  } catch (e) {
    clonedTrack?.stop()
    lease.release()
    throw e
  }
}

/**
 * A digital-zero probe over a raw `MediaStreamTrack`. `MicCheck` uses this to
 * take the same reading livekit-client runs in `detectSilence`: a window of
 * time-domain samples is digitally silent when every sample sits at the zero
 * line (128 for unsigned bytes, or 0). Non-zero audio on a live, enabled track
 * is `ready`; two silent reads confirm `digital_silence`. `readLevel` gives the
 * window's loudness for the near-silence check, because a mic that sends only a
 * faint hiss is not digitally silent: tiny negative samples round down to 127.
 *
 * It observes a `MediaStreamTrack` directly rather than a livekit `LocalTrack`,
 * because the microphone, the AV preview and the device picker each hand it a
 * plain track. It clones the track so tearing down never stops the caller's
 * capture, and releases its own lease on cleanup.
 */
export function createSilenceProbe(track: MediaStreamTrack) {
  const lease = audioContextController.acquire('createSilenceProbe')
  if (!lease) {
    throw new Error('Audio Context not supported on this browser')
  }
  const audioContext = lease.context

  // Held outside the try so the catch can stop it: a clone abandoned there keeps
  // capturing the mic for the life of the page.
  let clonedTrack: MediaStreamTrack | null = null
  try {
    clonedTrack = track.clone()
    const source = audioContext.createMediaStreamSource(
      new MediaStream([clonedTrack])
    )
    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 2048
    source.connect(analyser)
    const samples = new Uint8Array(analyser.fftSize)
    const floatSamples = new Float32Array(analyser.fftSize)

    // True when the current window carries no signal at all.
    const isSilent = () => {
      analyser.getByteTimeDomainData(samples)
      for (const sample of samples) {
        if (sample !== 128 && sample !== 0) return false
      }
      return true
    }

    // Root-mean-square of the current window, on the 0-1 full-scale range.
    const readLevel = () => {
      analyser.getFloatTimeDomainData(floatSamples)
      let sumOfSquares = 0
      for (const sample of floatSamples) sumOfSquares += sample * sample
      return Math.sqrt(sumOfSquares / floatSamples.length)
    }

    const cleanup = () => {
      source.disconnect()
      analyser.disconnect()
      clonedTrack?.stop()
      lease.release()
    }

    return { isSilent, readLevel, cleanup }
  } catch (e) {
    clonedTrack?.stop()
    lease.release()
    throw e
  }
}

/**
 * This is our version of the audio analyzer that uses an audio worklet
 * to process incoming frames of audio data, calculate its volume in dBFS,
 * and trigger a callback when the volume crosses a threshold.
 *
 * @param track - The audio track to analyze
 * @param threshold - The threshold in dBFS at which the callback should be triggered
 * @param onThresholdCross - The callback to trigger when the volume crosses the threshold
 * @returns A cleanup function that should be called when the analyzer is no longer needed
 */
export async function createAudioVolumeAnalyser({
  track,
  onThresholdCross,
  debug,
}: {
  track: LocalTrack<Track.Kind.Audio> | LocalAudioTrack | MediaStreamTrack
  onThresholdCross: (over: boolean) => void
  debug?: boolean
}) {
  const lease = audioContextController.acquire('createAudioVolumeAnalyser')

  if (!lease) {
    throw new Error('Audio Context not supported on this browser')
  }
  const audioContext = lease.context

  if (!track) {
    lease.release()
    return () => {}
  }

  // The meeting hands a livekit LocalTrack; MicCheck and the AV preview hand a
  // plain MediaStreamTrack. A LocalTrack carries a `.mediaStreamTrack`; a raw
  // track does not. Duck-type rather than `instanceof`, so this also runs where
  // the `MediaStreamTrack` global is absent.
  const mediaStreamTrack =
    'mediaStreamTrack' in track ? track.mediaStreamTrack : track

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const inHotReloadDev = import.meta.hot

  const workletUrl = inHotReloadDev
    ? MyWorkletProcessorUrl
    : MyWorkletProcessorUrlProd

  // Held outside the try so the catch can stop it. The clone is a live capture
  // of the mic; if createMediaStreamSource or the AudioWorkletNode constructor
  // throws after it exists, closing the context alone leaves the track running
  // for the life of the page — on Safari, with the recording indicator still
  // lit.
  let streamTrack: MediaStreamTrack | null = null
  try {
    const bandpass = audioContext.createBiquadFilter()
    bandpass.type = 'bandpass'
    bandpass.frequency.value = 1000
    bandpass.Q.value = 1

    // A rejection here (network, CSP) would otherwise escape before `cleanup`
    // reaches the caller, leaving the lease held and unreachable. The
    // controller loads each module once per context — calling addModule twice
    // would re-run the script and hit a duplicate registerProcessor.
    await audioContextController.loadWorklet(workletUrl)

    streamTrack = mediaStreamTrack.clone()
    const source = audioContext.createMediaStreamSource(
      new MediaStream([streamTrack])
    )

    const workletNode = new AudioWorkletNode(audioContext, 'bl-volume-analyzer')

    workletNode.port.onmessage = (event) => {
      onThresholdCross(event.data.isCrossed)
    }

    source.connect(bandpass).connect(workletNode)

    const interval = debug
      ? setInterval(() => {
          workletNode.port.postMessage({ type: 'debug' })
        }, 1000)
      : null

    const clonedTrack = streamTrack
    // Closing the context used to tear the whole graph down. On the shared
    // context the worklet node keeps its processor alive and keeps being fed
    // until it is disconnected by hand.
    const cleanup = async () => {
      workletNode.port.onmessage = null
      source.disconnect()
      bandpass.disconnect()
      workletNode.disconnect()
      clonedTrack.stop()
      if (interval) clearInterval(interval)
      lease.release()
    }

    return cleanup
  } catch (e) {
    streamTrack?.stop()
    lease.release()
    throw e
  }
}
